import React, { useEffect, useState } from 'react';
import { X, Search, FileText } from 'lucide-react';
import purchaseRequestService from '../../../services/purchaseRequestService';

/**
 * Search and pick ANY purchase request (any status/stage) to attach
 * procurement documents to. Not limited to requests currently sitting in
 * the procurement review stage - RFQ/Competitive Analysis usually happen
 * before a request is even approved, and PO/GRN usually happen after.
 */
const PurchaseRequestPickerModal = ({ onSelect, onClose }) => {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  const runSearch = async (term) => {
    try {
      setLoading(true);
      const res = await purchaseRequestService.getAllRequests({
        show_all: true,
        search: term || undefined,
        limit: 20,
      });
      setResults(res.data.data.requests || []);
    } catch (error) {
      console.error('Search purchase requests error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    runSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => runSearch(search), 300);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-16 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Select a Purchase Request
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by request ID, requester, vendor, or item..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm"
          />
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Searching...</div>
        ) : results.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">No purchase requests found</div>
        ) : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {results.map((r) => (
              <button
                key={r.id}
                onClick={() => onSelect(r)}
                className="w-full text-left px-3 py-2 rounded-md hover:bg-blue-50 border border-transparent hover:border-blue-200 flex items-center justify-between"
              >
                <div>
                  <span className="text-sm font-medium text-blue-600">{r.request_id}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    {r.requester_name} · {r.vendor_name}
                  </span>
                </div>
                <span className="text-xs text-gray-400 capitalize">{(r.current_stage || '').replace(/_/g, ' ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseRequestPickerModal;
